<script setup lang="ts">
import type { NuxtError } from "#app";

const props = defineProps<{
  error: NuxtError;
}>();

const statusCode = computed(() => Number(props.error?.statusCode) || 500);
const isNotFound = computed(() => statusCode.value === 404);

const title = computed(() =>
  isNotFound.value ? "Page not found" : "Something went wrong",
);

const message = computed(() => {
  if (isNotFound.value) {
    return "The page you requested could not be found. It may have been moved or removed.";
  }
  return props.error?.statusMessage || "An unexpected error occurred.";
});

const event = useRequestEvent();
if (event) {
  setResponseStatus(event, statusCode.value);
}

useSeoMeta({
  title: () => `${title.value} | Mont Blanc Treks`,
  description: message,
  robots: "noindex, nofollow",
});

useHead({
  bodyAttrs: {
    class: "error-page",
  },
  meta: [{ name: "robots", content: "noindex, nofollow" }],
});

const handleClear = async () => {
  await clearError({ redirect: "/" });
};
</script>

<template>
  <div>
    <AppHeader />
    <NuxtLoadingIndicator color="#f15822" :height="3" />
    <div id="smooth-wrapper">
      <div id="smooth-content">
        <main id="main-content">
          <div class="error-page-inner">
            <div class="content-wrapper">
              <div class="content error-content">
                <p class="error-code">{{ statusCode }}</p>
                <h1>{{ title }}</h1>
                <p>{{ message }}</p>
                <p class="error-actions">
                  <button type="button" class="button" @click="handleClear">
                    Back to homepage
                  </button>
                </p>
                <nav v-if="isNotFound" class="error-links" aria-label="Popular pages">
                  <ul>
                    <li>
                      <NuxtLink to="/classic-treks">Classic Treks</NuxtLink>
                    </li>
                    <li>
                      <NuxtLink to="/worldwide-treks">Worldwide Treks</NuxtLink>
                    </li>
                    <li>
                      <NuxtLink to="/get-in-touch">Get in touch</NuxtLink>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </main>
        <AppFooter />
      </div>
    </div>
  </div>
</template>

<style scoped>
.error-content {
  padding: 4rem 1.5rem 6rem;
  text-align: center;
  max-width: 40rem;
  margin: 0 auto;
}

.error-code {
  font-size: 0.875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.6;
  margin-bottom: 0.5rem;
}

.error-content h1 {
  margin-bottom: 1rem;
}

.error-content .button {
  display: inline-block;
  margin-top: 1.5rem;
  cursor: pointer;
}

.error-links {
  margin-top: 2.5rem;
}

.error-links ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  justify-content: center;
}

.error-links a {
  text-decoration: underline;
}
</style>
